// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Truncate from "@bg/App/Components/Truncate";
import WidgetTitle from "@bg/App/Components/WidgetTitle";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { xScreen } from "@bg/XDL/Utilities";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/NewsList/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Tab from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    tabs = "tabs",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.tabs]: any[];
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: WidgetProps) => {
    const parentPageId = state.links.pageTypesToNodeId["NewsListPage"];

    const parentPageLink = getPageLinkByNodeId(state, parentPageId);
    const pages: any = parentPageId ? getDataView(state.data.pages, { parentNodeId: parentPageId }) : {};
    let links: SafeDictionary<PageLinkModel> = {};
    if (pages && pages.value && pages.value.length > 0) {
        links = pages.value.reduce(
            (links: any, page: { nodeId: string }) => ({
                ...links,
                [page.nodeId]: getPageLinkByNodeId(state, page.nodeId),
            }),
            {},
        );
    }
    return {
        parentNodeId: parentPageId || "",
        location: getLocation(state),
        pages,
        links,
        parentPageLink,
        parameter: state.pages.newsList.getNewsPagesParameters[parentPageId] ?? {
            parentNodeId: parentPageId || "",
        },
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
    updateLoadParameter,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory & OwnProps;

export interface XNewsListStyles {
    titleText?: TypographyProps;
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    listWrapper?: InjectableCss;
    newsWrapper?: InjectableCss;
    summaryWrapper?: InjectableCss;
    secondSectionWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    titleSummaryWrapper?: InjectableCss;
    readMoreLink?: LinkPresentationProps;
    pagination?: PaginationPresentationProps;
    sectionWrapper?: InjectableCss;
    headerWrapper?: InjectableCss;
    headerIcon?: InjectableCss;
    sectionTitle?: InjectableCss;
    viewAll?: InjectableCss;
}

export const newsListViewStyles: XNewsListStyles = {
    sectionWrapper: {
        css: css`
            border-top: 1px solid ${getColor("common.ui3")};
            .news-tab {
                margin-right: 60px;
                ${xScreen("lte", "sm")} {
                    margin-right: 10px;
                }
            }
        `,
    },
    headerWrapper: {
        css: css`
            display: flex;
            margin-bottom: 24px;
        `,
    },
    headerIcon: {
        css: css`
            width: 55px;
            height: 61px;
            margin-right: 17px;
        `,
    },
    sectionTitle: {
        css: css`
            margin-right: 12px;
            margin-top: 18px;
            ${xScreen("lte", "xs")} {
                font-size: 30px;
            }
        `,
    },
    viewAll: {
        css: css`
            display: flex;
            margin-top: 15px;
        `,
    },
    listWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            flex-direction: row;

            ${xScreen("lte", "md")} {
                justify-content: center;
            }
        `,
    },
    secondSectionWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-direction: column;
            height: calc(100% - 217px);
        `,
    },
    titleSummaryWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        `,
    },
    titleText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
            height: 50px;
            color: ${getColor("common.ui5")};
        `,
    },
    newsWrapper: {
        css: css`
            margin-bottom: 15px;
            padding-top: 15px;
            min-width: 317px;
            display: flex;
            flex-direction: column;

            ${xScreen("lte", "md")} {
                max-width: 100%;
            }
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                border-top: 1px solid ${getColor("common.border")};
                padding-top: 15px;
            `,
        },
    },
    summaryWrapper: {
        css: css`
            margin-top: 8px;
            margin-bottom: 20px;
            p {
                color: ${getColor("common.ui5")};
                font-size: ${fontConfig.fontSize.xSmall}px;
            }
        `,
    },
    authorText: {
        css: css`
            font-weight: ${fontConfig.weight.bold};
            margin-right: 8px;
        `,
    },
    publishDateText: {
        css: css`
            background-color: ${getColor("text.main")};
            line-height: ${fontConfig.lineHeight.small};
            color: ${getColor("common.white")};
            padding: 7px 15px;
            position: absolute;
            left: 0;
            bottom: 0;
        `,
    },
    readMoreLink: {
        css: css`
            span {
                font-weight: ${fontConfig.weight.semiBold};
                letter-spacing: 0.2px;
                line-height: ${fontConfig.lineHeight.singleLineSmall};
                color: ${getColor("primary")} !important;
            }
        `,
    },
};

const styles = newsListViewStyles;

class XNewsList extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        if (!this.props.pages.value && !this.props.pages.isLoading && this.props.parentNodeId) {
            this.props.loadPagesForParent(this.props.parameter);
        }
    }

    componentDidUpdate() {
        if (!this.props.pages.value && !this.props.pages.isLoading && this.props.parentNodeId) {
            this.props.loadPagesForParent(this.props.parameter);
        }
    }

    render() {
        const {
            links,
            pages: { value: pages },
            fields,
        } = this.props;

        if (!pages || !this.props.parentNodeId) {
            return null;
        }

        const sortedAndPagedPages = pages
            .slice()
            .sort(
                (page1: any, page2: any) =>
                    new Date(page1.fields.publishDate).getTime() - new Date(page2.fields.publishDate).getTime(),
            )
            .reverse();

        const allowedTabs = fields.tabs && fields.tabs.length > 6 ? fields.tabs.slice(0, 6) : fields.tabs;
        return (
            <StyledWrapper {...styles.sectionWrapper}>
                <WidgetTitle
                    title={fields.title || ""}
                    viewAllText={translate("View All")}
                    viewAllLink={this.props.parentPageLink?.url}
                    icon="LatestNews"
                />
                {fields.tabs && fields.tabs.length > 0 && (
                    <TabGroup current={`${fields.tabs[0].fields.tabTitle}0`}>
                        {allowedTabs.map((item, index) => {
                            let taggedPages = sortedAndPagedPages.filter((page: any) => {
                                return page.fields.tags.includes(item.fields.tag);
                            });
                            if (taggedPages.length === 0) {
                                taggedPages = sortedAndPagedPages;
                            }
                            return (
                                <Tab
                                    key={`${fields.tabs[0].fields.tabTitle}0`}
                                    headline={item.fields.tabTitle}
                                    className="news-tab"
                                    tabKey={item.fields.tabTitle + index}
                                >
                                    <GridContainer>
                                        {taggedPages.map((page: PageProps, index: number) => {
                                            const imageObj = page.translatableFields?.openGraphImage;
                                            return (
                                                index < 4 && (
                                                    <GridItem
                                                        width={[12, 6, 6, 4, 3]}
                                                        // eslint-disable-next-line react/no-array-index-key
                                                        key={page.id + index}
                                                        css={css`
                                                            @media (max-width: 700px) {
                                                                width: 100%;
                                                                max-width: 100%;
                                                                flex-basis: 100%;
                                                            }
                                                        `}
                                                    >
                                                        <StyledWrapper {...styles.newsWrapper}>
                                                            <StyledWrapper
                                                                css={css`
                                                                    height: 215px;
                                                                    min-height: 215px;
                                                                    margin-bottom: 30px;
                                                                    position: relative;
                                                                    background-image: url(${imageObj[
                                                                        Object.keys(imageObj)[0]
                                                                    ]});
                                                                    background-size: cover;
                                                                `}
                                                            >
                                                                {page.fields["publishDate"] && (
                                                                    <Typography {...styles.publishDateText}>
                                                                        <LocalizedDateTime
                                                                            dateTime={
                                                                                new Date(page.fields["publishDate"])
                                                                            }
                                                                            options={{
                                                                                year: "numeric",
                                                                                month: "numeric",
                                                                                day: "numeric",
                                                                            }}
                                                                        />
                                                                    </Typography>
                                                                )}
                                                                <Link
                                                                    css={css`
                                                                        position: absolute;
                                                                        width: 100%;
                                                                        height: 100%;
                                                                    `}
                                                                    href={links[page.nodeId]?.url}
                                                                ></Link>
                                                            </StyledWrapper>

                                                            <StyledWrapper {...styles.secondSectionWrapper}>
                                                                <StyledWrapper {...styles.titleSummaryWrapper}>
                                                                    <Typography {...styles.titleText}>
                                                                        <Truncate lines={2} lineHeight={26}>
                                                                            {page.fields["title"]}
                                                                        </Truncate>
                                                                    </Typography>

                                                                    {(page.fields["newsSummary"] ||
                                                                        page.fields["newsContent"]) && (
                                                                        <StyledWrapper {...styles.summaryWrapper}>
                                                                            <Truncate lines={3} lineHeight={18}>
                                                                                {parse(
                                                                                    page.fields["newsSummary"] ||
                                                                                        page.fields["newsContent"],
                                                                                    parserOptions,
                                                                                )}
                                                                            </Truncate>
                                                                        </StyledWrapper>
                                                                    )}
                                                                </StyledWrapper>
                                                                <Link
                                                                    href={links[page.nodeId]?.url}
                                                                    {...styles.readMoreLink}
                                                                >
                                                                    {translate("Read More")}
                                                                </Link>
                                                            </StyledWrapper>
                                                        </StyledWrapper>
                                                    </GridItem>
                                                )
                                            );
                                        })}
                                    </GridContainer>
                                </Tab>
                            );
                        })}
                    </TabGroup>
                )}
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(XNewsList)),
    definition: {
        group: "XNGAGE" as any,
        displayName: "Blogs",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.tabs,
                editorTemplate: "ListField",
                displayName: "Tabs (Max number: 6 Tabs)",
                getDisplay: (item: HasFields) => {
                    return item.fields.tabTitle;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "tabTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "tag",
                        displayName: "Tag",
                        defaultValue: "",
                        editorTemplate: "TextField",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
